<template>
    <b-card
        no-body
        :border-variant="border ? 'light' : ''"
    >
        <div class="card-header">
            <div class="col-md-6 pl-0">
                <b-card-title>{{ productSelectorTitle }}</b-card-title>
            </div>
            <div class="col-md-6 pr-0 d-flex justify-content-end" v-if="isVisible && !hasConcernElements">
                <b-form-group class="mb-0 mr-1 w-100" v-if="showProductSearch">
                    <vue-autosuggest
                        ref="autocomplete"
                        :suggestions="suggestions"
                        :input-props="inputProps"
                        :get-suggestion-value="getSuggestionValue"
                        @selected="onSuggestionSelect"
                        @input="fetchResults"
                    >
                        <div slot-scope="{suggestion}">
                        <span class="pr-1">
                            <feather-icon class="text-primary" icon="CheckIcon" v-if="isSelected(suggestion)" />
                        </span>
                            {{suggestion.item.title}}
                        </div>
                    </vue-autosuggest>
                </b-form-group>
                <b-button
                    v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                    variant="primary"
                    class="btn-icon"
                    v-if="showAddButton"
                    @click="addProductClick"
                >
                    {{ $t('Component(IncludedProduct):add') }}
                </b-button>
            </div>
        </div>

        <b-alert
            variant="light"
            :show="showIncludedComponentsAndChemicalProductsInstruction"
            class="ml-2 mr-2"
        >
            <div class="alert-body">
                <span>{{ $t('Component(IncludedProduct):instruction') }}</span>
            </div>
        </b-alert>

        <Transition mode="out-in" name="fade">
            <div v-if="isVisible">
                <b-alert
                    variant="warning"
                    show
                    class="m-2"
                    v-if="hasConcernElements"
                >
                    <div class="alert-body">
                        <span>{{ $t('Component(IncludedProduct):cant-have-components') }}</span>
                    </div>
                </b-alert>
                <b-alert
                    variant="secondary"
                    show
                    class="m-2"
                    v-else-if="items.length === 0"
                >
                    <div class="alert-body">
                        <span>{{ emptyDescription }}</span>
                    </div>
                </b-alert>
                <validation-observer
                    v-else
                    ref="components"
                >
                    <b-table
                        :fields="fields"
                        :items="items"
                        responsive
                        @row-clicked="onRowClick"
                        hover
                    >
                        <!-- A virtual column -->
                        <template #cell(name)="data">
                            {{ data.item.title }}
                        </template>

                        <!-- A custom formatted column -->
                        <template #cell(id)="data">
                            {{ data.item.productId }}
                        </template>

                        <!-- A virtual composite column -->
                        <template #cell(supplier)="data">
                            {{ data.item.supplier ? data.item.supplier.title : '' }}
                        </template>

                        <template #cell(status)="data">
                            {{ data.item.status ? $t('Component(RequestStatusEnum):' + data.item.status) : '' }}
                        </template>

                        <template #cell(numberOfUnits)="data">
                            <validation-provider
                                #default="{ errors }"
                                :name="data.item.title + ' ' + $t('Component(IncludedProduct):field-number-of-units')"
                                rules="required|greaterThanZero|integer"
                            >
                                <b-form-input
                                    number
                                    :state="errors.length > 0 ? false:null"
                                    style="max-width: 120px;"
                                    type="number"
                                    :name="data.item.id + 'number_of_units'"
                                    @input="onNumberOfUnitsChange"
                                    min="1"
                                    v-model="data.item.numberOfUnits"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </template>

                        <template #cell(actions)="data">
                            <div class="text-right">
                                <b-button
                                    v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                                    variant="danger"
                                    class="btn-icon rounded-circle"
                                    @click="removeItem(data.item)"
                                >
                                    <feather-icon icon="Trash2Icon" />
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </validation-observer>
            </div>
            <div v-else class="m-2">
                <b-form-checkbox
                    v-model="allowForPurchased"
                    class="custom-control-primary"
                >
                    I would like to add components/chemical products for this purchased product.
                </b-form-checkbox>
            </div>
        </Transition>
    </b-card>
</template>
<script>
import {
    BAlert, BCardText, BTable, BCardTitle, BCard, BRow, BCol, BFormGroup, BButton, BCardGroup,
    BFormCheckbox, BFormInput
} from 'bootstrap-vue'
import {ProductAttribute, ProductCategorySelect} from "@core/components/product/index";
import Ripple from 'vue-ripple-directive'
import { VueAutosuggest } from 'vue-autosuggest'
import {ProductApi} from "@core/api";
import {ProductType} from "@/libs/product";
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {required, integer, greaterThanZero} from '@validations'

export default {
    components: {
        BCard, BFormInput, BCardText, BButton, ProductAttribute, ProductCategorySelect, BAlert, BRow,
        BCol, BFormGroup, BCardGroup, BTable, BCardTitle, BFormCheckbox, VueAutosuggest,
        ValidationProvider, ValidationObserver,
    },
    directives: {
        Ripple,
    },
    props: {
        type: {
            type: String,
            default: ProductType.PURCHASED
        },
        selected: {
            type: Array,
            default() {
                return [];
            }
        },
        showProductSearch: {
            default: true,
        },
        emptyDescription: {
            default() {
                switch (this.type) {

                    case ProductType.GROUP:
                        return this.$t('Component(IncludedProduct):empty-description-group');

                    case ProductType.OWN:
                        return this.$t('Component(IncludedProduct):empty-description-own');

                    default:
                        return this.$t('Component(IncludedProduct):empty-description');
                }
            }
        },
        border: {
            type: Boolean,
            default: false
        },
        showAddButton: {
            default: false,
            type: Boolean
        },

        hasConcernElements: {
            default: false,
            type: Boolean
        }
    },
    data() {
        return {
            allowForPurchased: false,
            inputProps: {
                id: 'autosuggest__input_ajax',
                placeholder: this.$t('Component(IncludedProduct):search-placeholder'),
                class: 'form-control w-100',
                name: 'ajax',
            },
            suggestions: [],
            searchTimer: null,
            fields: [
                { key: 'name', label: this.$t('Component(IncludedProduct):field-title') },
                { key: 'id', label: this.$t('Component(IncludedProduct):field-id') },
                { key: 'numberOfUnits', label: this.$t('Component(IncludedProduct):field-number-of-units') },
                { key: 'supplier', label: this.$t('Component(IncludedProduct):field-supplier') },
                { key: 'status', label: this.$t('Component(IncludedProduct):field-request-status') },
                { key: 'actions', label: this.$t('Component(IncludedProduct):field-actions'), class: 'text-right' }
            ],
            items: this.selected,
            required, greaterThanZero, integer,
        };
    },

    methods: {
        onNumberOfUnitsChange() {
            this.$refs.components.validate();
        },
        addProductClick() {
            this.$emit('addProduct');
        },
        getSuggestionValue() {
            return '';
        },
        onSuggestionSelect({item}) {
            if (!this.isSelected({item: item})) {
                this.items.push(item);
                this.emitChange();
            }
        },
        removeItem(item) {
            let key = this.items.findIndex((p) => p.id === item.id);

            if (key > -1) {
                this.items.splice(key, 1);
                this.emitChange();
            }
        },
        fetchResults(qs) {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            if (!qs) {
                this.suggestions = [];
                return;
            }
            this.searchTimer = setTimeout(() => {
                ProductApi.searchIncludedProducts(qs).then((r) => {
                    this.suggestions = [{data: r.data}];
                });
            }, 600);
        },
        isSelected({item}) {
            return this.items.includes(item);
        },
        emitChange() {
            this.$emit('change', this.items);
        },
        onRowClick(includedProduct) {
            this.$emit('clicked', includedProduct);
        }
    },
    computed: {
        productSelectorTitle() {
            switch (this.type) {
                case ProductType.FICTITIOUS:
                case ProductType.GROUP:
                    return this.$t('Page(NewProduct):Component(Text):included_products');

                case ProductType.PURCHASED_UNDEFINED_SUPPLIER:
                case ProductType.PURCHASED:
                case ProductType.OWN:
                case ProductType.COMPONENT_TO_PURCHASED_PRODUCT:
                case ProductType.COMPONENT_TO_COMPLEX_PURCHASED_PRODUCT:
                    return this.$t('Page(NewProduct):Component(Text):included_products_and_chemical_products')
            }
        },
        showIncludedComponentsAndChemicalProductsInstruction() {
            return [ProductType.PURCHASED, ProductType.OWN].includes(this.type);
        },
        isVisible() {
            //  HAM-140
            //  visible if product type is not purchased or purchased product contains components
            if (this.allowForPurchased) {
                return true;
            }
            return this.type !== ProductType.PURCHASED || this.items.length > 0;
        },
    },
    watch: {
        selected(value) {
            this.items = value;
        }
    }
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.autosuggest__results-item {
    cursor: pointer;
}
</style>
